import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Share2, Download, Trash2, ExternalLink, MapPin, User, Palette } from 'lucide-react';

interface ModelProps {
  fileName: string;
  description?: string;
  fileType?: string;
  fileSize?: number;
  signedUrl?: string;
  size?: number;
  captureType?: string;
  cameraType?: string;
  sceneType?: string;
  category?: string;
  createdAt?: number | string; // Extended to handle both timestamp and ISO string
  // Extended Collectiv3 properties - ALL OPTIONAL
  credits?: string;
  location?: string;
  KnowMore?: string;
}

interface SceneDetailsPanelProps {
  model: ModelProps;
  onClose?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

const SceneDetailsPanel: React.FC<SceneDetailsPanelProps> = ({ model, onClose, onShare, onDownload, onDelete }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const formatFileSize = (bytes?: number): string => {
    if (bytes === undefined || bytes === 0) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format dates (handles both number and string)
  const formatCreatedAt = (date: number | string) => {
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    return dateObj.toLocaleDateString();
  };

  // Get category icon for Collectiv3 scenes (optional enhancement)
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return <Palette className="w-3 h-3 mr-1" />;
      case 'people':
        return <User className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  console.log('[DEBUG] SceneDetailsPanel render:', {
  fileName: model.fileName,
  fileSize: model.fileSize,
  size: model.size,
  fileType: model.fileType,
  showCondition: (model.fileSize !== undefined || model.size !== undefined)
});

  return (
    <div 
      className={`fixed right-0 top-0 bottom-0 z-[1001] bg-[#161513]/60 backdrop-blur-sm transition-all duration-300 ease-in-out shadow-lg border-l border-[#0CB4E4]/30 flex flex-col items-center ${
        isCollapsed ? 'w-12' : 'w-80'
      }`}
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)} 
        className="absolute top-[40px] left-[-16px] w-8 h-8 bg-[#161513] rounded-full border border-[#0CB4E4]/30 flex items-center justify-center shadow-md"
        aria-label={isCollapsed ? "Expand panel" : "Collapse panel"} 
      >
        {isCollapsed ? <ChevronLeft className="w-5 h-5 text-gray-300" /> : <ChevronRight className="w-5 h-5 text-gray-300" />}
      </button>

      {!isCollapsed && (
        <div className="flex flex-col h-full w-full">
          <div className="flex-grow overflow-y-auto px-4 py-4 space-y-4">
            {/* Close Button */}
            {onClose && (
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 px-4 py-2 bg-[#161513] text-gray-300 hover:text-white hover:bg-[#161513]/80 rounded-lg transition-colors border border-[#0CB4E4]/30 shadow-sm"
                  title="Close viewer (ESC)"
                >
                  <span>Exit</span>
                </button>
              </div>
            )}
            
            <div>
              <h3 className="font-medium text-lg text-white tracking-wider">
                {model.fileName?.replace(/\.(gaus|gauss|splat|ply)$/, '') || 'Untitled Scene'}
              </h3>
              
              {/* ONLY NEW ADDITION: Category with icon for Collectiv3 scenes */}

              {model.description && (
                <div className="mb-4">
                  <div className="text-sm text-gray-200 leading-relaxed text-left whitespace-pre-wrap break-words">
                    {model.description}
                  </div>
                </div>
              )}

              {/* ONLY NEW ADDITION: Credits section for Collectiv3 scenes */}
              {model.credits && (
                <div className="mb-4">
                  <p className="text-sm text-[#0CB4E4] font-medium">
                    {model.credits}
                  </p>
                </div>
              )}

              {/* ONLY NEW ADDITION: Location section for Collectiv3 scenes */}
              {model.location && (
                <div className="mb-4">
                  <p className="inline-flex items-center gap-2 text-sm text-[#0CB4E4] hover:text-[#0CB4E4]/80 transition-colors font-medium"
                  >
                    <MapPin className="w-3 h-3" /> {model.location}
                  </p>
                </div>
              )}

              {/* ONLY NEW ADDITION: Know More link for Art scenes */}
              {model.KnowMore && model.category?.toLowerCase() === 'art' && (
                <div className="mb-4">
                  <a
                    href={model.KnowMore}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#0CB4E4] hover:text-[#0CB4E4]/80 transition-colors font-medium"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Know more about the art
                  </a>
                </div>
              )}

              {/* ORIGINAL: Action buttons - UNCHANGED */}
              <div className="flex gap-2 justify-center">
                {onShare && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onShare(); }}
                    className="p-2 bg-[#161513]/60 hover:bg-[#0CB4E4]/20 text-white rounded-lg transition-colors border border-[#0CB4E4]/30 flex items-center justify-center"
                    title="Share scene"
                  >
                    <Share2 className="w-4 h-4 transition-transform hover:scale-110" />
                  </button>
                )}
                {onDownload && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDownload(); }}
                    className="p-2 bg-[#161513]/60 hover:bg-[#0CB4E4]/20 text-white rounded-lg transition-colors border border-[#0CB4E4]/30 flex items-center justify-center"
                    title="Download scene"
                  >
                    <Download className="w-4 h-4 transition-transform hover:scale-110" />
                  </button>
                )} 
                {onDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="p-2 bg-[#161513]/60 hover:bg-red-900/20 text-white rounded-lg transition-colors border border-[#0CB4E4]/30 flex items-center justify-center"
                    title="Delete scene"
                  >
                    <Trash2 className="w-4 h-4 transition-transform hover:scale-110" />
                  </button>
                )}
              </div>
            </div>

            {/* ORIGINAL: Collapsible Scene Details - UNCHANGED except createdAt formatting */}
            <div className="w-full">
              <button 
                onClick={() => setShowDetails(!showDetails)} 
                className="text-sm text-white/70 font-semibold mb-2 underline"
              >
                {showDetails ? 'Hide Scene Details' : 'Show Scene Details'}
              </button>

              {showDetails && (
                <>
                  <div className="space-y-3 text-sm">
                    {model.fileType && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Type:</span>
                        <span>{model.fileType}</span>
                      </div>
                    )}
                    {(model.fileSize !== undefined || model.size !== undefined) && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Size:</span>
                        <span>{formatFileSize(model.fileSize || model.size)}</span>
                      </div>
                    )}
                    {model.captureType && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Capture Setup:</span>
                        <span>{model.captureType}</span>
                      </div>
                    )}
                    {model.cameraType && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Camera Type:</span>
                        <span>{model.cameraType}</span>
                      </div>
                    )}
                    {model.sceneType && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Scene Type:</span>
                        <span>{model.sceneType}</span>
                      </div>
                    )}
                    {model.createdAt && (
                      <div className="flex justify-between text-white/90">
                        <span className="text-gray-400">Created:</span>
                        <span>{formatCreatedAt(model.createdAt)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ORIGINAL: Navigation controls - COMPLETELY UNCHANGED */}
          <hr className="border-t border-white/10 mx-5" />

          <div className="p-5 space-y-4 bg-[#161513]/40 text-center">
            <h5 className="text-base font-medium text-white/80 mb-2">Navigation</h5>
            <div className="flex flex-col items-center text-white space-y-3">
              <span className="text-xs text-gray-400">Forward</span>
              <div className="key"><span>↑</span></div>
              <div className="flex justify-center gap-4">
                <div className="flex flex-col items-center"><div className="key"><span>←</span></div><span className="text-xs mt-1 text-gray-400">Turn</span></div>
                <div className="flex flex-col items-center"><div className="key"><span>↓</span></div><span className="text-xs mt-1 text-gray-400">Back</span></div>
                <div className="flex flex-col items-center"><div className="key"><span>→</span></div><span className="text-xs mt-1 text-gray-400">Turn</span></div>
              </div>
            </div>

            <h5 className="text-base font-medium text-white/80 mb-2">Advanced Controls</h5>
            <div className="flex flex-col items-center text-white space-y-4">
              <div className="flex justify-center gap-4">
                <div className="key"><span>Q</span><span className="text-xs text-gray-400">Down</span></div>
                <div className="key"><span>W</span><span className="text-xs text-gray-400">↑</span></div>
                <div className="key"><span>E</span><span className="text-xs text-gray-400">Up</span></div>
              </div>
              <div className="flex justify-center gap-4">
                <div className="key"><span>A</span><span className="text-xs text-gray-400">←</span></div>
                <div className="key"><span>S</span><span className="text-xs text-gray-400">↓</span></div>
                <div className="key"><span>D</span><span className="text-xs text-gray-400">→</span></div>
              </div>
              <p className="text-sm text-gray-400">Click and drag to turn around</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SceneDetailsPanel;